import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import SendIcon from '@mui/icons-material/Send';
import moment from 'moment';

import store from '../../../utilities/store';
import { StateManager } from '../../../utilities/stateManager';
import SendToLendersDialog from '../FinanceComponents/SendToLendersDialog';

const STATUS_COLORS = {
  pending: '#ff9800',
  reviewed: '#2196f3',
  sent: '#9c27b0',
  approved: '#4caf50',
  declined: '#f44336',
};

// Credit apps for the buyer and co-buyer, with the send. Reps work the deal from
// the Sales tab, so the application the lenders need sits next to the people it
// belongs to — not one tab over.
export default function CreditAppPanel({ car = {} }) {
  const buyerId = car.buyer?.id;
  const cobuyerId = car.cobuyer?.id;

  const [loading, setLoading] = React.useState(false);
  const [buyerApps, setBuyerApps] = React.useState([]);
  const [coApps, setCoApps] = React.useState([]);
  const [sendOpen, setSendOpen] = React.useState(false);

  const load = React.useCallback(() => {
    if (!buyerId && !cobuyerId) { setBuyerApps([]); setCoApps([]); return undefined; }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      buyerId ? store.getCreditAppsForCustomer(buyerId) : Promise.resolve([]),
      cobuyerId ? store.getCreditAppsForCustomer(cobuyerId) : Promise.resolve([]),
    ])
      .then(([b, c]) => { if (!cancelled) { setBuyerApps(b); setCoApps(c); } })
      .catch(() => { if (!cancelled) { setBuyerApps([]); setCoApps([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [buyerId, cobuyerId]);

  React.useEffect(() => load(), [load]);

  // A send flips the app to "sent" server-side — pick that up on close.
  const handleClose = () => { setSendOpen(false); load(); };

  return (
    <>
      <Box sx={{ px: 2, py: 1.75, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">Loading credit apps…</Typography>
          </Box>
        ) : (
          <>
            <AppRow label="Buyer" customer={car.buyer} app={buyerApps[0]} />
            {cobuyerId && <AppRow label="Co-Buyer" customer={car.cobuyer} app={coApps[0]} />}
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setSendOpen(true)}
            disabled={loading || !buyerApps.length}
          >
            Send to Lenders
          </Button>
        </Box>
      </Box>

      <SendToLendersDialog open={sendOpen} onClose={handleClose} car={car} />
    </>
  );
}

function AppRow({ label, customer, app }) {
  const name = customer?.display_name;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', width: 64, flexShrink: 0 }}>
        {label}
      </Typography>

      {!name ? (
        <Typography variant="body2" color="text.secondary">Not assigned</Typography>
      ) : !app ? (
        <Typography variant="body2" color="text.secondary">No credit app on file</Typography>
      ) : (
        <>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            onClick={() => StateManager.openCreditAppModal(app.id)}
          >
            {app.time ? moment(app.time, 'YYYY-MM-DD HH:mm:ss').format('MMM D, YYYY') : 'Undated app'}
          </Typography>
          <Chip
            size="small"
            label={app.status || 'pending'}
            sx={{
              height: 18, fontSize: '0.65rem', textTransform: 'capitalize',
              color: '#fff', bgcolor: STATUS_COLORS[app.status] || '#9e9e9e',
            }}
          />
        </>
      )}
    </Box>
  );
}
